import { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { toast } from 'react-toastify';

const getSpeechRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

/** Ensures only one field listens at a time */
let activeSessionStop = null;

const buildValue = (base, spoken) => {
  const prefix = (base ?? '').toString();
  const text = (spoken ?? '').toString().trim();
  if (!text) return prefix;
  if (!prefix) return text;
  return `${prefix.trimEnd()} ${text}`;
};

/** Scroll input/textarea so the latest spoken text stays visible */
export const scrollFieldToEnd = (element) => {
  if (!element) return;
  const run = () => {
    try {
      element.scrollTop = element.scrollHeight;
      element.scrollLeft = element.scrollWidth;
      // Keep caret at the end so typed/spoken text stays in view
      const len = element.value?.length ?? 0;
      if (typeof element.setSelectionRange === 'function') {
        element.setSelectionRange(len, len);
      }
    } catch (_) {
      /* some input types (number) block setSelectionRange */
    }
  };
  run();
  requestAnimationFrame(run);
};

/**
 * Reusable mic button for speech-to-text.
 * Click to start listening; click again to stop.
 * Transcript is written into the bound field via onResult(nextValue).
 */
const SpeechToTextMicButton = ({
  value = '',
  onResult,
  disabled = false,
  lang = 'en-IN',
  size = 'small',
  sx = {},
  inputRef = null,
  onListeningChange,
}) => {
  const [listening, setListening] = useState(false);
  const [starting, setStarting] = useState(false);

  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const baseValueRef = useRef('');
  const restartTimerRef = useRef(null);
  const langRef = useRef(lang);
  const onResultRef = useRef(onResult);
  const valueRef = useRef(value);
  const inputElRef = useRef(inputRef);
  const onListeningChangeRef = useRef(onListeningChange);
  const stopRef = useRef(() => {});

  langRef.current = lang;
  onResultRef.current = onResult;
  valueRef.current = value ?? '';
  inputElRef.current = inputRef;
  onListeningChangeRef.current = onListeningChange;

  const setListeningState = (next) => {
    setListening(next);
    if (typeof onListeningChangeRef.current === 'function') {
      onListeningChangeRef.current(next);
    }
  };

  const clearRestartTimer = () => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  };

  const resolveInputEl = () => {
    const ref = inputElRef.current;
    if (!ref) return null;
    return ref.current ?? ref;
  };

  /** Always prefer what is currently visible in the input (respects backspace/edits) */
  const getLiveFieldValue = () => {
    const el = resolveInputEl();
    if (el && typeof el.value === 'string') {
      return el.value;
    }
    return (valueRef.current ?? '').toString();
  };

  const pushResult = (nextValue) => {
    if (typeof onResultRef.current !== 'function') return;
    const text = nextValue == null ? '' : String(nextValue);
    valueRef.current = text;
    onResultRef.current(text);
    scrollFieldToEnd(resolveInputEl());
  };

  /** Sync base from live input — including empty string after user deletes text */
  const syncBaseFromLiveField = () => {
    const latest = getLiveFieldValue();
    baseValueRef.current = latest;
    valueRef.current = latest;
    return latest;
  };

  const hardStopRecognition = () => {
    clearRestartTimer();
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (!recognition) return;

    recognition.onstart = null;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;

    try {
      recognition.stop();
    } catch (_) {
      /* already stopped */
    }
  };

  const stopListening = () => {
    shouldListenRef.current = false;
    setListeningState(false);
    setStarting(false);

    if (activeSessionStop === stopRef.current) {
      activeSessionStop = null;
    }

    // Keep whatever user currently has (including deletions). Do not push stale base text back.
    syncBaseFromLiveField();
    hardStopRecognition();
  };

  stopRef.current = stopListening;

  const attachHandlers = (recognition, SpeechRecognitionAPI) => {
    recognition.onstart = () => {
      setListeningState(true);
      setStarting(false);
    };

    recognition.onresult = (event) => {
      // If user backspaced/edited, never resurrect the removed text
      const live = getLiveFieldValue();
      if (live.length < baseValueRef.current.length) {
        baseValueRef.current = live;
      }

      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = (result?.[0]?.transcript || '').trim();
        if (!transcript) continue;

        if (result.isFinal) {
          // Commit finals into base immediately so long dictation never stalls
          baseValueRef.current = buildValue(baseValueRef.current, transcript);
        } else {
          interim = interim ? `${interim} ${transcript}` : transcript;
        }
      }

      const nextValue = interim
        ? buildValue(baseValueRef.current, interim)
        : baseValueRef.current;

      pushResult(nextValue);
    };

    recognition.onerror = (event) => {
      const error = event?.error;

      // Normal while Chrome pauses / restarts
      if (error === 'aborted' || error === 'no-speech') return;

      shouldListenRef.current = false;
      setListeningState(false);
      setStarting(false);
      hardStopRecognition();
      if (activeSessionStop === stopRef.current) activeSessionStop = null;

      if (error === 'not-allowed' || error === 'service-not-allowed') {
        toast.error('Microphone permission denied. Allow mic access and try again.');
        return;
      }
      if (error === 'network') {
        toast.error(
          'Speech service network error. Check internet, or try Chrome with a stable connection.',
        );
        return;
      }
      if (error === 'audio-capture') {
        toast.error('No microphone found. Connect a mic and try again.');
        return;
      }
      toast.error(`Speech recognition error: ${error || 'unknown'}`);
    };

    recognition.onend = () => {
      if (!shouldListenRef.current) {
        setListeningState(false);
        setStarting(false);
        if (activeSessionStop === stopRef.current) activeSessionStop = null;
        recognitionRef.current = null;
        return;
      }

      // Chrome ends sessions periodically — use live field text (honors backspace), then restart
      syncBaseFromLiveField();

      restartTimerRef.current = setTimeout(() => {
        if (!shouldListenRef.current) return;
        try {
          const next = new SpeechRecognitionAPI();
          next.continuous = true;
          next.interimResults = true;
          next.lang = langRef.current;
          next.maxAlternatives = 1;
          attachHandlers(next, SpeechRecognitionAPI);
          recognitionRef.current = next;
          next.start();
        } catch (_) {
          // One more retry after a short delay
          restartTimerRef.current = setTimeout(() => {
            if (!shouldListenRef.current) return;
            try {
              const retry = new SpeechRecognitionAPI();
              retry.continuous = true;
              retry.interimResults = true;
              retry.lang = langRef.current;
              retry.maxAlternatives = 1;
              attachHandlers(retry, SpeechRecognitionAPI);
              recognitionRef.current = retry;
              retry.start();
            } catch (__) {
              shouldListenRef.current = false;
              setListeningState(false);
              setStarting(false);
              if (activeSessionStop === stopRef.current) activeSessionStop = null;
              recognitionRef.current = null;
              toast.info('Speech paused. Click the mic to continue speaking.');
            }
          }, 300);
        }
      }, 80);
    };
  };

  const startListening = () => {
    const SpeechRecognitionAPI = getSpeechRecognition();
    if (!SpeechRecognitionAPI) {
      toast.error('Speech-to-text is not supported in this browser. Use Chrome or Edge.');
      return;
    }

    if (typeof onResultRef.current !== 'function') {
      toast.error('Speech input is not connected to this field.');
      return;
    }

    if (activeSessionStop && activeSessionStop !== stopRef.current) {
      activeSessionStop();
    }

    clearRestartTimer();
    hardStopRecognition();

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = langRef.current;
    recognition.maxAlternatives = 1;

    // Start from what is actually in the input now (after backspace/edits), never old speech cache
    syncBaseFromLiveField();
    shouldListenRef.current = true;
    recognitionRef.current = recognition;
    activeSessionStop = stopRef.current;

    attachHandlers(recognition, SpeechRecognitionAPI);

    try {
      setStarting(true);
      recognition.start();
      scrollFieldToEnd(resolveInputEl());
    } catch (_) {
      shouldListenRef.current = false;
      setStarting(false);
      setListeningState(false);
      recognitionRef.current = null;
      if (activeSessionStop === stopRef.current) activeSessionStop = null;
      toast.error('Could not start speech recognition. Try again.');
    }
  };

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || starting) return;
    if (listening || shouldListenRef.current) {
      stopListening();
    } else {
      startListening();
    }
  };

  useEffect(
    () => () => {
      shouldListenRef.current = false;
      if (activeSessionStop === stopRef.current) activeSessionStop = null;
      hardStopRecognition();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const supported = Boolean(getSpeechRecognition());
  const tooltip = !supported
    ? 'Speech-to-text not supported in this browser'
    : listening
      ? 'Stop listening'
      : 'Start speaking';

  return (
    <Tooltip title={tooltip}>
      <span>
        <IconButton
          type="button"
          size={size}
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleToggle}
          disabled={disabled || !supported || starting}
          aria-label={listening ? 'Stop speech to text' : 'Start speech to text'}
          aria-pressed={listening}
          sx={{
            color: listening ? '#d32f2f' : 'var(--color-heading, #24266C)',
            p: 0.5,
            animation: listening ? 'speechMicPulse 1.2s ease-in-out infinite' : 'none',
            '@keyframes speechMicPulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.12)' },
              '100%': { transform: 'scale(1)' },
            },
            ...sx,
          }}
        >
          {starting ? (
            <CircularProgress size={18} thickness={5} />
          ) : listening ? (
            <MicOffIcon fontSize="small" />
          ) : (
            <MicIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SpeechToTextMicButton;
